import { DocumentNode } from "@apollo/client";
import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import { ApiKey } from "types/generated";
import { UserManagementActivationStatus } from "views/Account/Settings/types";

import FormFieldApiKeyGroups from "../components/FormFieldApiKeyGroups";
import { useUpdateApiKey } from "../hooks/useUpdateApiKey";
import { ManageGroupsFields } from "./types";

type ApiKeysManageGroupsDrawerProps = {
  apiKey: Pick<ApiKey, "id" | "name" | "description" | "teams">;
  activationStatus: UserManagementActivationStatus;
  refetchQueriesOnUpdate?: DocumentNode[];
};

const ApiKeysManageGroupsDrawer = createDrawer(
  ({ apiKey, activationStatus, refetchQueriesOnUpdate }: ApiKeysManageGroupsDrawerProps) => {
    const drawer = useModal();

    const groupsForm = useForm<ManageGroupsFields>({
      defaultValues: {
        groups: apiKey.teams.map((team) => ({ value: team })) || [],
      },
      mode: "onChange",
    });

    const {
      handleSubmit,
      formState: { isDirty },
    } = groupsForm;

    const handleCloseDrawer = () => {
      drawer.hide();
    };

    const { onUpdate, loading } = useUpdateApiKey({
      apiKeyId: apiKey.id,
      callback: handleCloseDrawer,
      refetchQueries: refetchQueriesOnUpdate,
    });

    const handleSubmitClick = () => {
      handleSubmit(onSubmit)();
    };

    const onSubmit: SubmitHandler<ManageGroupsFields> = (formFields) => {
      onUpdate({
        name: apiKey.name,
        description: apiKey.description,
        teams: formFields.groups.map((group) => group.value),
        oidcClaimMappings: null,
      });
    };

    return (
      <DrawerForm isDirty={isDirty}>
        <FormProvider {...groupsForm}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Manage groups" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody>
            <Box direction="column" gap="large">
              <FormFieldApiKeyGroups name="groups" activationStatus={activationStatus} isOptional />
            </Box>

            <DrawerFooter>
              <DrawerFooterActions>
                <Button variant="secondary" onPress={handleCloseDrawer}>
                  Cancel
                </Button>
                <Button variant="primary" onPress={handleSubmitClick} loading={loading}>
                  Save
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </DrawerBody>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showApiKeysManageGroupsDrawer = createDrawerTrigger(ApiKeysManageGroupsDrawer);
